import { FC, useId } from 'react';

import { BIZY3_POSES, type Bizy3Arm } from './bizy3-poses.generated';

type BizyPeekingHandsProps = {
	/** Render width/height — must match the paired `Bizy` peeking body size. */
	size: number;
	className?: string;
	strokeColor?: string;
	strokeOpacity?: number;
};

const VIEW_BOX = '0 0 322 324';
const DEFAULT_ARM_COLOR = '#051236';

export const PEEKING_HAND_PATHS = {
	left: BIZY3_POSES.peeking.leftArm.d,
	right: BIZY3_POSES.peeking.rightArm.d,
} as const;

const ArmTextureFilter: FC<{ arm: Bizy3Arm; id: string }> = ({ arm, id }) => (
	<filter id={id} x={-80} y={-80} width={480} height={480} filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
		<feFlood floodOpacity="0" result="background" />
		<feBlend in="SourceGraphic" in2="background" mode="normal" result="shape" />
		<feTurbulence
			type="fractalNoise"
			baseFrequency={`${arm.texture.frequency[0]} ${arm.texture.frequency[1]}`}
			numOctaves={arm.texture.numOctaves}
			seed={arm.texture.seed}
			result="noise"
		/>
		<feDisplacementMap in="shape" in2="noise" scale={arm.texture.scale} xChannelSelector="R" yChannelSelector="G" />
	</filter>
);

/** Exact Bizy3 peeking arms rendered outside a crop container. */
export const BizyPeekingHands: FC<BizyPeekingHandsProps> = ({ size, className, strokeColor, strokeOpacity }) => {
	const id = useId().replaceAll(':', '');
	const arms = [BIZY3_POSES.peeking.leftArm, BIZY3_POSES.peeking.rightArm];
	const color = strokeColor ?? DEFAULT_ARM_COLOR;

	return (
		<svg className={className} width={size} height={size} viewBox={VIEW_BOX} fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
			<defs>
				{arms.map((arm, index) => (
					<ArmTextureFilter key={index} arm={arm} id={`${id}-peeking-arm-${index}`} />
				))}
			</defs>
			{arms.map((arm, index) => (
				<path key={index} d={arm.d} fill={color} filter={`url(#${id}-peeking-arm-${index})`} opacity={strokeOpacity} />
			))}
		</svg>
	);
};
